import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import Icon from '@/components/Icon';

const OVERFLOW_TOLERANCE = 2;

/** A single horizontal timeline with controls beside, rather than over, clips. */
const StudioTimelineRail = forwardRef(function StudioTimelineRail({
  children,
  colors,
  style,
  contentContainerStyle,
  accessibilityLabel = 'Clip timeline',
  previousLabel = 'Previous clips',
  nextLabel = 'Next clips',
  resetKey,
}, forwardedRef) {
  const scrollRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const [offset, setOffset] = useState(0);
  const maxOffset = Math.max(0, contentWidth - viewportWidth);
  const hasOverflow = viewportWidth > 0 && maxOffset > OVERFLOW_TOLERANCE;
  const canPrevious = hasOverflow && offset > OVERFLOW_TOLERANCE;
  const canNext = hasOverflow && offset < maxOffset - OVERFLOW_TOLERANCE;

  const attachRef = useCallback((node) => {
    scrollRef.current = node;
    if (typeof forwardedRef === 'function') forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  }, [forwardedRef]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ x: 0, animated: false });
    setOffset(0);
  }, [resetKey]);

  // Keep the visible position reachable when clips disappear or the panel
  // gets wider, without changing it when a selected card merely rerenders.
  useEffect(() => {
    if (!viewportWidth) return;
    const next = hasOverflow ? Math.min(offset, maxOffset) : 0;
    if (next === offset) return;
    scrollRef.current?.scrollTo({ x: next, animated: false });
    setOffset(next);
  }, [hasOverflow, maxOffset, offset, viewportWidth]);

  const advance = (direction) => {
    const next = Math.max(0, Math.min(maxOffset, offset + direction * viewportWidth * 0.75));
    scrollRef.current?.scrollTo({ x: next, animated: true });
    setOffset(next);
  };

  const arrow = (direction, enabled) => (
    <View style={styles.arrowSlot}>
      {hasOverflow ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={direction < 0 ? previousLabel : nextLabel}
          accessibilityHint={`Scrolls ${direction < 0 ? 'backward' : 'forward'} in ${accessibilityLabel}`}
          accessibilityState={{ disabled: !enabled }}
          disabled={!enabled}
          onPress={() => advance(direction)}
          style={({ hovered, pressed }) => [
            styles.arrow,
            { borderColor: colors?.border_color_secondary, backgroundColor: colors?.solid_color_secondary },
            (hovered || pressed) && enabled && { backgroundColor: colors?.solid_color_tertiary },
            !enabled && styles.disabled,
          ]}
        >
          <Icon name={direction < 0 ? 'chevron-left' : 'chevron-right'} size={14} color={colors?.text_color_primary} />
        </Pressable>
      ) : null}
    </View>
  );

  return (
    <View style={[styles.container, style]}>
      {arrow(-1, canPrevious)}
      <ScrollView
        ref={attachRef}
        horizontal
        accessibilityLabel={accessibilityLabel}
        accessibilityRole={Platform.OS === 'web' ? 'region' : undefined}
        tabIndex={Platform.OS === 'web' ? 0 : undefined}
        style={styles.track}
        contentContainerStyle={[styles.content, contentContainerStyle]}
        showsHorizontalScrollIndicator
        persistentScrollbar
        scrollEventThrottle={32}
        onLayout={(event) => setViewportWidth(Math.max(0, event.nativeEvent.layout.width))}
        onContentSizeChange={(width) => setContentWidth(Math.max(0, width))}
        onScroll={(event) => setOffset(Math.max(0, event.nativeEvent.contentOffset.x))}
      >
        {children}
      </ScrollView>
      {arrow(1, canNext)}
    </View>
  );
});

const styles = StyleSheet.create({
  container: { flexDirection: 'row', alignItems: 'center', minWidth: 0, flexShrink: 0, gap: 6 },
  // Slots stay the same width when overflow changes, so displaying arrows
  // cannot reduce the viewport and create a self-sustaining overflow state.
  arrowSlot: { width: 32, flexShrink: 0, alignItems: 'center', justifyContent: 'center' },
  arrow: { width: 32, height: 40, borderWidth: 1, borderRadius: 9, alignItems: 'center', justifyContent: 'center' },
  disabled: { opacity: 0.35 },
  track: { flexGrow: 1, flexShrink: 1, flexBasis: 0, minWidth: 0, alignSelf: 'stretch' },
  content: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, paddingBottom: 8 },
});

export default StudioTimelineRail;
